import type { Precio, RubroInfo, SerieKey } from './types';

/**
 * Catálogo estático de lo que publica la API hoy (rubros, categorías, presentaciones y unidad de cada una),
 * tomado de `contract-fixtures/ejemplo-ultimos.json`. Sirve para armar formularios (alertas, widgets,
 * calculadoras) sin esperar a la red; lo que llegue de `/v1/rubros` y de los precios se mezcla con
 * `catalogoDesdeDatos` para no perder categorías nuevas.
 */
export interface CategoriaCatalogo {
  clave: string;
  /** Presentaciones conocidas; vacío si la fuente no distingue ('' en la API se convierte en null). */
  presentaciones: string[];
  unidad: string;
  /** Además de las presentaciones hay fuentes que publican la categoría sin presentación (null). */
  sinPresentacion: boolean;
}

export interface RubroCatalogo {
  rubro: string;
  categorias: CategoriaCatalogo[];
}

const PIE_CANAL = ['en_pie', 'en_canal'];
const PROD_CONS = ['productor', 'consumidor'];
const cat = (
  clave: string,
  presentaciones: string[] = [],
  unidad = 'kg',
  sinPresentacion = presentaciones.length === 0,
): CategoriaCatalogo => ({ clave, presentaciones, unidad, sinPresentacion });

const HORTALIZAS = [
  'aji_dulce',
  'ajo',
  'apio',
  'batata',
  'berenjena',
  'brocoli',
  'calabacin',
  'cebolla',
  'cebollin',
  'chayota',
  'cilantro',
  'coliflor',
  'lechuga',
  'papa',
  'pepino',
  'pimenton_invernadero',
  'repollo',
  'tomate_manzano',
  'tomate_perita',
  'yuca',
  'zanahoria',
];

export const CATALOGO: readonly RubroCatalogo[] = [
  {
    rubro: 'bovino',
    categorias: [
      cat('toro', PIE_CANAL),
      cat('vaca', PIE_CANAL),
      cat('maute', ['en_pie']),
      cat('novillo', PIE_CANAL),
    ],
  },
  {
    rubro: 'bufalino',
    categorias: [
      cat('bufalo', PIE_CANAL),
      cat('bufala', PIE_CANAL),
      cat('baute', ['en_pie']),
      cat('bumaute', ['en_pie']),
    ],
  },
  {
    rubro: 'porcino',
    categorias: [
      cat('cerdo', PIE_CANAL),
      cat('cerdo_industrial', PIE_CANAL),
      cat('cerda', PIE_CANAL),
      cat('lechon', ['en_pie'], 'unidad'),
    ],
  },
  {
    rubro: 'lacteos',
    categorias: [
      cat('leche_vaca', ['caliente', 'fria', 'queseras'], 'L', true),
      cat('leche_bufala', [], 'L'),
      cat('queso'),
    ],
  },
  { rubro: 'hortalizas', categorias: HORTALIZAS.map((c) => cat(c, PROD_CONS)) },
  {
    rubro: 'frutas',
    categorias: [
      ...['aguacate', 'durazno_1era', 'fresa', 'mora'].map((c) => cat(c, PROD_CONS)),
      cat('higos', ['productor']),
      cat('papaya'),
      cat('platano'),
    ],
  },
];

/** Rubros de ganado en pie/canal: los que usan el rendimiento y la calculadora de lotes. */
export const RUBROS_GANADO: readonly string[] = ['bovino', 'bufalino', 'porcino'];

export const esGanado = (rubro: string): boolean => RUBROS_GANADO.includes(rubro);

export const rubrosCatalogo = (catalogo: readonly RubroCatalogo[] = CATALOGO): string[] =>
  catalogo.map((r) => r.rubro);

export function categoriasDe(
  rubro: string,
  catalogo: readonly RubroCatalogo[] = CATALOGO,
): CategoriaCatalogo[] {
  return catalogo.find((r) => r.rubro === rubro)?.categorias ?? [];
}

export function presentacionesDe(
  rubro: string,
  categoria: string,
  catalogo: readonly RubroCatalogo[] = CATALOGO,
): string[] {
  return categoriasDe(rubro, catalogo).find((c) => c.clave === categoria)?.presentaciones ?? [];
}

/** Unidad de venta de una categoría (kg por defecto; 'L' en leches, 'unidad' en lechón). */
export function unidadDe(rubro: string, categoria: string): string {
  return categoriasDe(rubro).find((c) => c.clave === categoria)?.unidad ?? 'kg';
}

/** Rubro al que pertenece una categoría conocida (null si no está en el catálogo). */
export function rubroDeCategoria(categoria: string): string | null {
  return CATALOGO.find((r) => r.categorias.some((c) => c.clave === categoria))?.rubro ?? null;
}

/** Todas las series (rubro + categoría + presentación) del catálogo; sin presentación → null. */
export function seriesDelCatalogo(rubro?: string): SerieKey[] {
  return CATALOGO.filter((r) => !rubro || r.rubro === rubro).flatMap((r) =>
    r.categorias.flatMap((c) =>
      [...c.presentaciones, ...(c.sinPresentacion ? [null] : [])].map((presentacion) => ({
        rubro: r.rubro,
        categoria: c.clave,
        presentacion,
      })),
    ),
  );
}

/**
 * Mezcla el catálogo estático con lo que informa la API (`/v1/rubros`) y con los precios recibidos:
 * añade rubros, categorías y presentaciones nuevas sin quitar nada. Conserva el orden del catálogo
 * y agrega lo nuevo al final.
 */
export function catalogoDesdeDatos(
  rubros: readonly RubroInfo[] = [],
  precios: readonly Precio[] = [],
): RubroCatalogo[] {
  const mapa = new Map<string, Map<string, CategoriaCatalogo>>();
  for (const r of CATALOGO) {
    mapa.set(
      r.rubro,
      new Map(r.categorias.map((c) => [c.clave, { ...c, presentaciones: [...c.presentaciones] }])),
    );
  }
  const asegurar = (rubro: string, categoria: string, unidad?: string): CategoriaCatalogo => {
    const porCategoria = mapa.get(rubro) ?? new Map<string, CategoriaCatalogo>();
    mapa.set(rubro, porCategoria);
    const existente = porCategoria.get(categoria) ?? cat(categoria, [], unidad, false);
    porCategoria.set(categoria, existente);
    return existente;
  };
  for (const p of precios) {
    const c = asegurar(p.rubro, p.categoria, p.unidad);
    if (!p.presentacion) c.sinPresentacion = true;
    else if (!c.presentaciones.includes(p.presentacion)) c.presentaciones.push(p.presentacion);
  }
  for (const r of rubros) for (const c of r.categorias) asegurar(r.rubro, c);
  return [...mapa].map(([rubro, categorias]) => ({
    rubro,
    categorias: [...categorias.values()].map((c) => ({
      ...c,
      sinPresentacion: c.sinPresentacion || c.presentaciones.length === 0,
    })),
  }));
}
